import { Alignment, StringUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ComponentFactory, Image, ImageProps, Label, Tab, Tabset, TabStyle } from "@mcleod/components";
import { AutogenLayoutTabsetLiveDemoStyleAlignment } from "./autogen/AutogenLayoutTabsetLiveDemoStyleAlignment";

interface TabDef {
    caption?: string;
    countLabelText?: string;
    displaysDivider?: boolean;
    headingDescrItems: string[];
    headingImageName?: ImageName;
    number: number;
    secondImageProps?: Partial<ImageProps>;
}

export class TabsetLiveDemoStyleAlignment extends AutogenLayoutTabsetLiveDemoStyleAlignment {
    private panelHeaderExpanded = true;
    private tabDefsBoth: TabDef[] = TabsetLiveDemoStyleAlignment.buildTabDefsBoth();
    private tabDefsCaptions: TabDef[] = TabsetLiveDemoStyleAlignment.buildTabDefsCaptions();
    private tabDefsImages: TabDef[] = TabsetLiveDemoStyleAlignment.buildTabDefsImages();
    private tabsetTest: Tabset;

    private static buildTabDefsBoth(): TabDef[] {
        return [
            {
                caption: "First Tab",
                headingDescrItems: [
                    "A short caption"
                ],
                number: 1
            },
            {
                caption: "Second Tab",
                displaysDivider: true,
                headingDescrItems: [
                    "A leading image (configured using the tab's imageName property)",
                    "A short caption"
                ],
                headingImageName: ImageName.documentException,
                number: 2
            },
            {
                caption: "Third Tab With Long Caption",
                headingDescrItems: [
                    "A leading image (configured using the tab's imageName property)",
                    "A long caption, which will commonly need to be truncated",
                    "An additional image, which was programmatically added to the tab heading"
                ],
                headingImageName: ImageName.documentException,
                number: 3,
                secondImageProps: {
                    name: ImageName.circleError,
                    color: "error",
                    marginLeft: 6,
                    rowBreak: false
                }
            },
            {
                caption: "Fourth Tab",
                countLabelText: "77",
                headingDescrItems: [
                    "A short caption",
                    "A sample count label, which was programmatically added to the tab heading"
                ],
                number: 4
            }
        ]
    }

    private static buildTabDefsCaptions(): TabDef[] {
        return [
            {
                caption: "First Tab",
                headingDescrItems: [
                    "A short caption"
                ],
                number: 1
            },
            {
                caption: "Second Tab",
                displaysDivider: true,
                headingDescrItems: [
                    "A leading image (configured using the tab's imageName property)",
                    "A short caption",
                    "A divider border between it and the subsequent tab"
                ],
                number: 2
            },
            {
                caption: "Third Tab With Long Caption",
                headingDescrItems: [
                    "A long caption, which will commonly need to be truncated"
                ],
                number: 3
            },
            {
                caption: "Fourth Tab",
                countLabelText: "77",
                headingDescrItems: [
                    "A short caption",
                    "A sample count label, which was programmatically added to the tab heading"
                ],
                number: 4
            }
        ]
    }

    private static buildTabDefsImages(): TabDef[] {
        return [
            {
                headingDescrItems: [
                    "An image (configured using the tab's imageName property"
                ],
                headingImageName: ImageName.people,
                number: 1
            },
            {
                displaysDivider: true,
                headingDescrItems: [
                    "An image (configured using the tab's imageName property)",
                    "A divider border between it and the subsequent tab"
                ],
                headingImageName: ImageName.apiDot,
                number: 2
            },
            {
                headingDescrItems: [
                    "An image (configured using the tab's imageName property)",
                ],
                headingImageName: ImageName.calculator,
                number: 3
            },
            {
                countLabelText: "77",
                headingDescrItems: [
                    "An image (configured using the tab's imageName property)",
                    "A sample count label, which was programmatically added to the tab heading"
                ],
                headingImageName: ImageName.truck,
                number: 4
            }
        ]
    }

    override onLoad() {
        this.radioboxStyle.setSelectedFromValue("underlined");
        this.radioboxAlignment.setSelectedFromValue("top");
        this.radioboxCaptionsImages.setSelectedFromValue("both");
    }

    buttonChevronOnClick() {
        if (this.panelHeaderExpanded) {
            this.collapsePanelHeader();
        } else {
            this.expandPanelHeader();
        }
    }

    public collapsePanelHeader() {
        this.panelHeaderExpanded = false;
        this.setChevronImageRotation();
        this.panelHeader.collapse({ speed: 400 });
    }

    public async expandPanelHeader() {
        this.panelHeaderExpanded = true;
        this.setChevronImageRotation();
        this.panelHeader.expand({ speed: 400 });
    }

    private setChevronImageRotation() {
        this.buttonChevron.imageRotation = this.panelHeaderExpanded ? 0 : 90;
    }

    buttonCreateTabsetOnClick() {
        this.remove(this.tabsetTest);
        this.createTabset();
    }

    private createTabset() {
        const headingMaxWidth = Number(this.textboxHeadingMaxWidth.text);
        const headingMinWidth = Number(this.textboxHeadingMinWidth.text);
        this.tabsetTest = new Tabset({
            fillHeight: true,
            fillRow: true,
            id: "tabsetTest",
            tabAlignment: this.radioboxAlignment.selectedValue,
            tabHeadingMaxWidth: headingMaxWidth > 0 ? headingMaxWidth : null,
            tabHeadingMinWidth: headingMinWidth > 0 ? headingMinWidth : null,
            tabStyle: this.radioboxStyle.selectedValue
        });
        const tabDefs = this.getTabDefs();
        tabDefs?.forEach(tabDef => this.createTab(tabDef));
        this.add(this.tabsetTest);
    }

    private getTabDefs(): TabDef[] {
        switch (this.radioboxCaptionsImages.selectedValue) {
            case "both":
                return this.tabDefsBoth;
            case "captions":
                return this.tabDefsCaptions;
            case "images":
                return this.tabDefsImages;
            default:
                return null;
        }
    }

    private createTab(tabDef: TabDef): Tab {
        const tab = new Tab({ caption: tabDef.caption, id: `tab${tabDef.number}` });
        const labels = ComponentFactory.createMultipleCommon([
            {
                fontSize: "large",
                marginBottom: 25,
                text: `This is tab #${tabDef.number}`
            },
            {
                text: "Its header/selector includes:"
            }
        ]);
        labels.push(...ComponentFactory.createBulletedList(tabDef.headingDescrItems));
        tab.add(...labels);
        tab.displaysDividerBorder = this.checkboxDivider.checked && tabDef.displaysDivider === true;
        this.tabsetTest.add(tab);
        tab.imageName = tabDef.headingImageName;
        if (!StringUtil.isEmptyString(tabDef.countLabelText)) {
            const countLabel = new Label({
                color: "primary",
                fontBold: true,
                paddingTop: 0,
                paddingBottom: 0,
                rowBreak: false,
                text: `(${tabDef.countLabelText})`
            });
            tab.heading.insertBefore(countLabel, tab.heading.insertComponentsBeforeThis);
        }
        if (tabDef.secondImageProps != null) {
            const secondImage = new Image(tabDef.secondImageProps);
            tab.heading.insertBefore(secondImage, tab.heading.insertComponentsBeforeThis);
        }
        return tab;
    }

    radioboxAlignmentAfterSelection() {
        this.syncDividerCheckbox();
    }

    radioboxStyleAfterSelection() {
        this.syncDividerCheckbox();
    }

    private syncDividerCheckbox() {
        const style = this.radioboxStyle.selectedValue;
        const alignment = this.radioboxAlignment.selectedValue;
        const enable = (alignment === Alignment.LEFT || alignment === Alignment.RIGHT) && style === TabStyle.UNDERLINED;
        if (!enable) {
            this.checkboxDivider.enabled = false;
            this.checkboxDivider.checked = false;
        } else if (this.checkboxDivider.enabled === false) {
            this.checkboxDivider.enabled = true;
        }
    }
}
